// safety/incidents-and-injuries/pages/IncidentsSettingsPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Settings, Plus, Pencil, Trash2, Loader2, AlertTriangle, Bell, Search, UserX } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Badge } from "../../../../components/ui/badge";
import { Switch } from "../../../../components/ui/switch";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../../components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../../../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
} from "../../../../components/ui/alert-dialog";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../../../constants/permissions.constants";
import AccessRestricted from "../../../../components/AccessRestricted";
import { toast } from "sonner";
import * as apiSvc from "../../../../utils/safety-incidents-api";
import type { EntityRef } from "../../../../types/incident.types";

const IncidentsSettingsPage: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("employer-entities");

    // Employer Entities state
    const [entities, setEntities] = useState<EntityRef[]>([]);
    const [entitiesLoading, setEntitiesLoading] = useState(false);
    const [showEntityDialog, setShowEntityDialog] = useState(false);
    const [editingEntity, setEditingEntity] = useState<EntityRef | null>(null);
    const [entityName, setEntityName] = useState("");
    const [entitySubmitting, setEntitySubmitting] = useState(false);
    const [entityToDelete, setEntityToDelete] = useState<EntityRef | null>(null);
    const [entityDeleteLoading, setEntityDeleteLoading] = useState(false);

    // Notifications tab state
    const [notifUsers, setNotifUsers] = useState<apiSvc.IncidentNotificationUser[]>([]);
    const [notifLoading, setNotifLoading] = useState(false);
    const [notifSearch, setNotifSearch] = useState("");
    const [notifPage, setNotifPage] = useState(1);
    const [notifTotalPages, setNotifTotalPages] = useState(0);
    const [notifTotalCount, setNotifTotalCount] = useState(0);
    const [notifTogglingId, setNotifTogglingId] = useState<number | null>(null);

    useEffect(() => {
        if (activeTab === "employer-entities") {
            fetchEntities();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== "notifications") return;
        const timer = setTimeout(() => {
            fetchNotificationUsers(notifPage, notifSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [activeTab, notifPage, notifSearch]);

    const fetchEntities = async () => {
        setEntitiesLoading(true);
        try {
            const rows = await apiSvc.listEmployerEntities();
            setEntities(rows);
        } catch (err) {
            console.error("Failed to load employer entities:", err);
        } finally {
            setEntitiesLoading(false);
        }
    };

    const fetchNotificationUsers = async (page: number, search: string) => {
        setNotifLoading(true);
        try {
            const { users, totalCount, totalPages } = await apiSvc.listNotificationUsers({
                page,
                limit: 15,
                search,
            });
            setNotifUsers(users);
            setNotifTotalCount(totalCount);
            setNotifTotalPages(totalPages);
        } catch (err) {
            console.error("Failed to load notification users:", err);
            toast.error("Failed to load staff list");
        } finally {
            setNotifLoading(false);
        }
    };

    const handleToggleNotification = async (userId: number) => {
        setNotifTogglingId(userId);
        try {
            const isNotified = await apiSvc.toggleNotificationUser(userId);
            setNotifUsers((prev) =>
                prev.map((u) => (u.id === userId ? { ...u, isNotified } : u))
            );
            toast.success(`Notifications ${isNotified ? "enabled" : "disabled"} for this user`);
        } catch (err: any) {
            toast.error(err.message || "Failed to update notification settings");
        } finally {
            setNotifTogglingId(null);
        }
    };

    const openAddEntityDialog = () => {
        setEditingEntity(null);
        setEntityName("");
        setShowEntityDialog(true);
    };

    const openEditEntityDialog = (entity: EntityRef) => {
        setEditingEntity(entity);
        setEntityName(entity.name);
        setShowEntityDialog(true);
    };

    const handleSaveEntity = async () => {
        if (!entityName.trim()) {
            toast.error("Employer entity name is required");
            return;
        }
        setEntitySubmitting(true);
        try {
            if (editingEntity) {
                const updated = await apiSvc.updateEmployerEntity(editingEntity.id, entityName.trim());
                toast.success("Employer entity updated");
                setEntities((prev) =>
                    prev.map((e) => (e.id === editingEntity.id ? updated : e))
                );
                setShowEntityDialog(false);
            } else {
                const created = await apiSvc.createEmployerEntity(entityName.trim());
                toast.success("Employer entity created");
                setEntities((prev) => [...prev, created]);
                setShowEntityDialog(false);
            }
        } catch (err: any) {
            toast.error(err.message || "Failed to save employer entity");
        } finally {
            setEntitySubmitting(false);
        }
    };

    const handleDeleteEntity = async () => {
        if (!entityToDelete) return;
        setEntityDeleteLoading(true);
        try {
            await apiSvc.deleteEmployerEntity(entityToDelete.id);
            setEntities((prev) => prev.filter((e) => e.id !== entityToDelete.id));
            toast.success("Employer entity deleted");
            setEntityToDelete(null);
        } catch (err: any) {
            toast.error(err.message || "Failed to delete employer entity");
        } finally {
            setEntityDeleteLoading(false);
        }
    };

    if (!ability.can(ACTIONS.READ, RESOURCES.INCIDENT_INJURY_SETTINGS)) {
        return <AccessRestricted />;
    }

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
            {/* Back Button */}
            <button
                onClick={() => navigate("/safety/incidents")}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
            >
                <ChevronLeft size={18} />
                <span className="text-sm font-medium">Back to Incidents &amp; Injuries</span>
            </button>

            {/* Header */}
            <div className="flex items-center gap-3">
                <Settings size={28} className="text-gray-900" />
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                        Incidents &amp; Injuries Settings
                    </h1>
                    <p className="text-gray-500 text-sm font-medium">
                        Manage reference data used across incident reports
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit flex h-auto mb-2">
                    <TabsTrigger
                        value="employer-entities"
                        className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
                    >
                        Employer Entity
                    </TabsTrigger>
                    <TabsTrigger
                        value="notifications"
                        className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
                    >
                        Notifications
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="employer-entities" className="mt-0">
                    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
                        {/* Header Row */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                                <h4 className="font-bold text-gray-900 text-base">
                                    Employer Entities
                                </h4>
                                <p className="text-gray-500 text-sm font-medium">
                                    Manage the employer entities available on incident reports
                                </p>
                            </div>
                            <Button
                                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold shrink-0"
                                onClick={openAddEntityDialog}
                            >
                                <Plus size={16} />
                                <span>Add Employer Entity</span>
                            </Button>
                        </div>

                        {entitiesLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 size={24} className="animate-spin text-gray-400" />
                            </div>
                        ) : entities.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-sm font-medium text-gray-500">
                                    No employer entities yet. Add your first employer entity to get started.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {entities.map((entity) => (
                                    <div
                                        key={entity.id}
                                        className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                                    >
                                        <p className="text-sm font-bold text-gray-900">
                                            {entity.name}
                                        </p>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                onClick={() => openEditEntityDialog(entity)}
                                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                                title="Edit employer entity"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                onClick={() => setEntityToDelete(entity)}
                                                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                                title="Delete employer entity"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="notifications" className="mt-0">
                    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
                        {/* Header Row */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                                <h4 className="font-bold text-gray-900 text-base flex items-center gap-2">
                                    <Bell size={18} className="text-gray-400" />
                                    Submission Notifications
                                </h4>
                                <p className="text-gray-500 text-sm font-medium">
                                    Choose who receives an email when a new incident is submitted
                                </p>
                            </div>
                            <div className="relative w-full sm:w-72 shrink-0">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <Input
                                    placeholder="Search staff..."
                                    className="pl-9 bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                                    value={notifSearch}
                                    onChange={(e) => {
                                        setNotifSearch(e.target.value);
                                        setNotifPage(1);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Desktop Table */}
                        <div className="hidden md:block border border-gray-100 rounded-2xl overflow-hidden">
                            <Table>
                                <TableHeader className="bg-gray-50/50">
                                    <TableRow>
                                        <TableHead className="font-semibold text-gray-600 px-6 py-4">Name</TableHead>
                                        <TableHead className="font-semibold text-gray-600 px-6 py-4">Department</TableHead>
                                        <TableHead className="font-semibold text-gray-600 px-6 py-4">User Role</TableHead>
                                        <TableHead className="font-semibold text-gray-600 px-6 py-4 text-center">Email Notifications</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {notifLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center py-16">
                                                <Loader2 size={24} className="animate-spin text-gray-400 mx-auto" />
                                            </TableCell>
                                        </TableRow>
                                    ) : notifUsers.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center py-16 text-gray-500">
                                                <div className="flex flex-col items-center gap-2">
                                                    <UserX size={32} className="text-gray-200" />
                                                    <p className="text-sm font-medium">No staff found</p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        notifUsers.map((user) => (
                                            <TableRow key={user.id} className="hover:bg-gray-50/50 transition-colors">
                                                <TableCell className="px-6 py-4">
                                                    <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{user.name}</p>
                                                    <p className="text-xs text-gray-500">{user.email}</p>
                                                </TableCell>
                                                <TableCell className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">{user.department}</TableCell>
                                                <TableCell className="px-6 py-4">
                                                    <Badge variant="outline" className="font-normal border-gray-200 text-xs text-gray-500">
                                                        {user.userRole}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="px-6 py-4">
                                                    <div className="flex items-center justify-center gap-3">
                                                        <span className={`text-xs font-medium ${user.isNotified ? "text-blue-600" : "text-gray-400"}`}>
                                                            {user.isNotified ? "Subscribed" : "Off"}
                                                        </span>
                                                        <Switch
                                                            checked={user.isNotified}
                                                            disabled={notifTogglingId === user.id}
                                                            onCheckedChange={() => handleToggleNotification(user.id)}
                                                        />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Mobile List */}
                        <div className="block md:hidden divide-y divide-gray-100 border border-gray-100 rounded-2xl overflow-hidden">
                            {notifLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 size={24} className="animate-spin text-gray-400" />
                                </div>
                            ) : notifUsers.length === 0 ? (
                                <div className="text-center py-12">
                                    <p className="text-sm font-medium text-gray-500">No staff found</p>
                                </div>
                            ) : (
                                notifUsers.map((user) => (
                                    <div key={user.id} className="p-4 flex items-center justify-between bg-white">
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-gray-900 truncate">{user.name}</p>
                                            <p className="text-xs text-gray-500 truncate">{user.userRole} • {user.department}</p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className={`text-[10px] font-bold uppercase tracking-wider ${user.isNotified ? "text-blue-600" : "text-gray-400"}`}>
                                                {user.isNotified ? "Subscribed" : "Off"}
                                            </span>
                                            <Switch
                                                checked={user.isNotified}
                                                disabled={notifTogglingId === user.id}
                                                onCheckedChange={() => handleToggleNotification(user.id)}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Pagination */}
                        {notifTotalPages > 1 && (
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                <p className="text-sm text-gray-500">
                                    Showing <span className="font-medium">{((notifPage - 1) * 15) + 1}</span> to <span className="font-medium">{Math.min(notifPage * 15, notifTotalCount)}</span> of <span className="font-medium">{notifTotalCount}</span> staff
                                </p>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={notifPage === 1 || notifLoading}
                                        onClick={() => setNotifPage((prev) => prev - 1)}
                                    >
                                        <ChevronLeft className="h-4 w-4 mr-1" />
                                        Previous
                                    </Button>
                                    <div className="flex items-center gap-1 font-medium text-sm px-2 bg-gray-50 rounded-lg h-9">
                                        <span className="text-blue-600 font-bold">{notifPage}</span>
                                        <span className="text-gray-300">/</span>
                                        <span>{notifTotalPages}</span>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={notifPage === notifTotalPages || notifLoading}
                                        onClick={() => setNotifPage((prev) => prev + 1)}
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Info Alert */}
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
                            <div className="p-1 h-fit bg-blue-100 rounded-lg">
                                <Bell size={16} className="text-blue-600" />
                            </div>
                            <div className="text-sm">
                                <p className="font-semibold text-blue-900">About Submission Notifications</p>
                                <p className="text-blue-700/80 mt-0.5">
                                    When a new incident is submitted, an email notification is sent to the person who
                                    submitted it and to every user toggled "Subscribed" here.
                                </p>
                            </div>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Add/Edit Employer Entity Dialog */}
            <Dialog open={showEntityDialog} onOpenChange={setShowEntityDialog}>
                <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <DialogHeader className="p-6 pb-4">
                        <DialogTitle className="text-lg font-bold text-gray-900">
                            {editingEntity ? "Edit Employer Entity" : "Add Employer Entity"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="px-6 pb-6 space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-sm font-bold text-gray-900">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <Input
                                placeholder="e.g., Idarah Pty Ltd"
                                className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                                value={entityName}
                                onChange={(e) => setEntityName(e.target.value)}
                            />
                        </div>
                        <div className="flex items-center justify-end gap-3 pt-2">
                            <Button
                                variant="outline"
                                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                                onClick={() => setShowEntityDialog(false)}
                                disabled={entitySubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5"
                                onClick={handleSaveEntity}
                                disabled={entitySubmitting}
                            >
                                {entitySubmitting ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : editingEntity ? (
                                    "Save Changes"
                                ) : (
                                    "Add Employer Entity"
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal — Employer Entity */}
            <AlertDialog open={!!entityToDelete} onOpenChange={(open) => { if (!open) setEntityToDelete(null); }}>
                <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <AlertTriangle size={20} className="text-red-500" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Employer Entity</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Are you sure you want to delete <span className="font-bold text-gray-700">"{entityToDelete?.name}"</span>? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                            disabled={entityDeleteLoading}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <Button
                            className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
                            onClick={handleDeleteEntity}
                            disabled={entityDeleteLoading}
                        >
                            {entityDeleteLoading ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                "Delete Employer Entity"
                            )}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default IncidentsSettingsPage;
